import { useCallback } from 'react';
/**
 * The custom property each edge is published as, on the root element.
 *
 * `theme-base.css` turns them into the document's `scroll-padding`, so a
 * control that receives focus — or a heading an anchor jumps to — is scrolled
 * clear of the bar instead of underneath it (WCAG 2.4.11). The toast host
 * and the shell's `<main>` read the bottom one to sit above the bottom bar.
 */
export const VIEWPORT_INSET_PROPERTY = {
    top: '--app-inset-top',
    bottom: '--app-inset-bottom',
};
/**
 * Publish how much of one edge of the viewport a fixed or sticky bar covers.
 *
 * Measured rather than written down, because none of these bars has one
 * height: the tutor's phone header is two rows and the student's one, the
 * runner's footer grows a notice when a save fails, and every one of them
 * grows with zoom and with a longer Spanish label. A number in a stylesheet
 * would be right for one of those and hide focus behind the others.
 *
 * A bar that is not covering anything — `display: none` at a width where it
 * is not used, or back in the flow on a short screen — publishes 0. One
 * `ResizeObserver` per bar, which only calls back when the bar changes size,
 * plus `resize` for the case where a media query changes its position and
 * not its size.
 */
export function useViewportInset(edge) {
    return useCallback((element) => {
        if (!element)
            return;
        const root = element.ownerDocument.documentElement;
        const view = element.ownerDocument.defaultView;
        const property = VIEWPORT_INSET_PROPERTY[edge];
        const update = () => {
            const style = getComputedStyle(element);
            const covers = style.display !== 'none' && (style.position === 'fixed' || style.position === 'sticky');
            root.style.setProperty(property, `${covers ? Math.ceil(element.getBoundingClientRect().height) : 0}px`);
        };
        update();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        observer?.observe(element);
        view?.addEventListener('resize', update);
        return () => {
            observer?.disconnect();
            view?.removeEventListener('resize', update);
            root.style.removeProperty(property);
        };
    }, [edge]);
}
//# sourceMappingURL=viewport-inset.js.map